import React, {
  useEffect,
  useState,
  createContext,
  useCallback,
  useContext,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "../Redux/authApi";
import { clearUser, setProfile } from "../Redux/authSlice";
import { identifyUser } from "../lib/mixpanel";

// Create the AuthContext
const AuthContext = createContext();
let pendingUserRequest = null;

const AuthProvider = ({ children }) => {
  const { user: reduxUserData, loading: reduxLoading } = useSelector(
    (state) => state.auth
  );
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const fetchUser = useCallback(async ({ silent = false } = {}) => {
    if (!pendingUserRequest) {
      pendingUserRequest = getUser().finally(() => {
        pendingUserRequest = null;
      });
    }

    try {
      if (!silent) setLoading(true);
      const response = await pendingUserRequest;
      setUser(response?.data?.user || null);
      await dispatch(setProfile(response?.data?.user));
    } catch (error) {
      console.error("Error fetching user:", error);
      setUser(null);
      await dispatch(clearUser());
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (reduxUserData) {
      setUser(reduxUserData);
      setLoading(false);
    } else {
      fetchUser({ silent: false });
    }
  }, [reduxUserData, fetchUser]);

  useEffect(() => {
    if (!user) return;
    identifyUser(user);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading: loading || reduxLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthProvider;
